// cSpell:ignore Gebouw Privaat
// "Add settlement" on a unit that asks which kind first (design 2026-10-16,
// C): a unit inside a building offers "Gebouw" (the settlement of the
// building, made on the building), "Privaat" (the unit's own costs) and a
// final settlement on departure per leaving tenant; a unit on its own offers
// a settlement and the departures. See ISettledDeparture for a departure.
// The chosen kind's small line sits under the select; in the open menu a
// departure shows it too, so two leaving tenants stay apart.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Modal, Select, TextStyle } from "@rentiohq/web-shared/dist/components";
import React, { useState } from "react";
import { fmtDate } from "../scenes/SettlementInfo/SettlementForm/steps/shared";
import { ContractRef } from "../types";
import {
  getNewSettlementChoices,
  INewSettlementChoice,
  NEW_SETTLEMENT_BUILDING,
  NEW_SETTLEMENT_UNIT,
} from "./newSettlementChoices.utils";
import { OptionInfo } from "./NewSettlementModal.styles";

const renderOption = (props: { data: INewSettlementChoice }) => (
  <>
    <div>{props.data.label}</div>
    {props.data.infoInMenu && <OptionInfo>{props.data.info}</OptionInfo>}
  </>
);

interface INewSettlementModalProps {
  // Leases that can get a departure settlement (see departureCandidates).
  candidates: ContractRef[];
  // A unit inside a building, not one on its own.
  isInBuilding: boolean;
  // On a unit inside a building whose costs the user may settle: opens the
  // settlement of the building.
  onChooseBuilding?: () => void;
  // The unit's own settlement, or a departure (its lease id).
  onChoose: (departureContractId?: string) => void;
  onClose: () => void;
}

export const NewSettlementModal: React.FC<INewSettlementModalProps> = ({
  candidates,
  isInBuilding,
  onChooseBuilding,
  onChoose,
  onClose,
}) => {
  const choices = getNewSettlementChoices({
    isInBuilding,
    canSettleBuilding: !!onChooseBuilding,
    departures: candidates.map(contract => ({
      id: contract.id,
      tenantName: contract.tenantName,
      until: fmtDate(contract.endDate),
    })),
  });
  const [choice, setChoice] = useState<string>(choices[0].value);
  const chosen = choices.find(option => option.value === choice) ?? choices[0];

  const handleNext = () => {
    if (chosen.value === NEW_SETTLEMENT_BUILDING) {
      onChooseBuilding?.();
      return;
    }
    onChoose(chosen.value === NEW_SETTLEMENT_UNIT ? undefined : chosen.value);
  };

  return (
    <Modal
      width="small"
      heading={getLocalizedText("costs.overview.add_settlement")}
      onClose={onClose}
      actions={[
        {
          content: getLocalizedText("costs.action.cancel"),
          onClick: onClose,
          appearance: "outline",
        },
        {
          content: getLocalizedText("costs.action.next"),
          onClick: handleNext,
          appearance: "primary",
        },
      ]}
    >
      <Select
        id="new-settlement-kind"
        label={getLocalizedText("settlement.departure.choose_label")}
        value={chosen.value}
        options={choices}
        optionRender={renderOption}
        onChange={(option: { value?: string } | null) =>
          setChoice(option?.value ?? choices[0].value)
        }
      />
      <div style={{ marginTop: 12 }}>
        <TextStyle variation="subdued" size="small">
          {chosen.info}
        </TextStyle>
      </div>
    </Modal>
  );
};
